import { Box, Card, Grid, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import componentStylesGeneric from "assets/theme/views/admin/generic.js";
import clsx from "clsx";
import GenericButton from "components/Buttons/GenericButton";
import FormDropdown from "components/FormFields/FormDropdown";
import LocationHeader from "components/Headers/LocationHeader";
import PageTitleHeader from "components/Headers/PageTitleHeader";
import { deleteContractApproval, submitContractApproval } from "crud/contractApproval.crud";
import { listContractApprovalWorkCenterEmployee } from "crud/contractApprovalWorkCenterEmployee.crud";
import { ConditionGuard } from "guards/ConditionGuard";
import { useDataset } from "hooks/useDataset";
import useFilterSync from "hooks/useFilterSync";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { connect, useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { Link } from "react-router-dom/cjs/react-router-dom";
import { listContractApprovalWorkCenterEmployeeAction } from "redux/actions/contractApprovalWorkCenterEmployeeAction";
import getDataSetByNameAction from "redux/actions/datasetsAction";
import { showGenericTwoButtonDialogAction } from "redux/actions/dialogsAction";
import { getWorkCenterEmployeeDocumentAction } from "redux/actions/workCentersEmployerAction";
import { DIALOG } from "utils/actionsConsts";
import { DATASET } from "utils/const";
import { getKeyValueByCode } from "utils/datasets";
import ContractApprovalDetailsTable from "../components/ContractApprovalDetailsTable";
import { ContractApprovalRequestDetailsCard } from "../components/ContractApprovalRequestDetailsCard";
import ContractApprovalWorkerDetailsDialog from "../components/ContractApprovalWorkerDetailsDialog";
import ContractApprovalWorkerDialog from "../components/ContractApprovalWorkerDialog";

const useStylesGeneric = makeStyles(componentStylesGeneric);

const useStylesLocal = makeStyles((theme) => ({
  cardMobilePadding: {
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
    },
  },
  fullWidthMobileButton: {
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  footerActions: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    marginTop: "20px",
    gap: "20px",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
      gap: theme.spacing(1),
      "& .MuiButton-root": {
        width: "100%",
      },
    },
  },
}));

function ContractApprovalCreateInner({ props, defaultFormValues }) {
  const classes = useStylesGeneric();
  const localClasses = useStylesLocal();

  const location = useLocation();
  const hasInitialSearchParams = (location.search || "") !== "";
  const [filtersReady, setFiltersReady] = useState(!hasInitialSearchParams);

  const [openDialog, setOpenDialog] = React.useState(false);
  const [openDetailsDialog, setOpenDetailsDialog] = React.useState(false);
  const [showSearchModal, setShowSearchModal] = React.useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const { new_contract_approval } = useSelector((state) => state.contractApprovalState) || {};

  const { control, watch, getValues, handleSubmit, trigger, setValue, formState } = useFormContext();

  const filterSync = useFilterSync({
    defaultValues: defaultFormValues,
    syncPagination: true,
    pagination: {
      pageIndexKey: "pageIndex",
      pageSizeKey: "pageSize",
    },
    onFilterChange: () => {
      setFiltersReady(true);
    },
  });

  const pageIndex = watch("pageIndex") ?? 1;

  const { data: PROVINCES } = useDataset({ name: DATASET.PROVINCES });

  const provinceId = watch("provinceId");
  const provinceKeyValue = useMemo(() => getKeyValueByCode(PROVINCES, provinceId), [PROVINCES, provinceId]);
  const queryClient = useQueryClient();

  const { data: EMPLOYMENT_CENTERS } = useDataset(
    { name: DATASET.EMPLOYMENT_CENTERS, parentKeyValue: provinceKeyValue },
    {
      enabled: !!provinceKeyValue,
    }
  );

  const handleChangePage = (_, value) => {
    setValue("pageIndex", value);
    filterSync.updatePagination(value);
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
    setShowSearchModal(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    queryClient.invalidateQueries({
      queryKey: ["listEmployee", new_contract_approval?.requestNumber ?? ""],
    });
  };

  const handleOpenDetailsDialog = (id) => {
    setSelectedId(id);
    setOpenDetailsDialog(true);
  };

  const handleCloseDetailsDialog = () => {
    setOpenDetailsDialog(false);
    setSelectedId(null);
  };

  useEffect(() => {
    if (!new_contract_approval) {
      handleOpenDialog();
    }
  }, [new_contract_approval]);

  const dispatch = useDispatch();

  const fetchContractApprovalWorkersList = useCallback(() => {
    return listContractApprovalWorkCenterEmployee(new_contract_approval?.requestNumber ?? "", pageIndex);
  }, [new_contract_approval, pageIndex]);

  const { data: listEmployee, isLoading } = useQuery({
    queryKey: ["listEmployee", new_contract_approval?.requestNumber, pageIndex],
    queryFn: fetchContractApprovalWorkersList,
    enabled: !!new_contract_approval?.requestNumber && filtersReady,
  });

  const { mutateAsync: submitRequest } = useMutation(
    ["submitContractApprovalRequest"],
    async () =>
      submitContractApproval({
        id: new_contract_approval?.requestNumber,
        provinceId: getValues().provinceId,
        employmentCenterId: getValues().employmentCenterId,
      }),
    {
      onSuccess: () => {
        dispatch({
          type: DIALOG.SHOW,
          payload: {
            showGenericDialog: true,
            genericDialogData: {
              type: DIALOG.ONE_BUTTON_TYPE,
              isSuccess: true,
              goBack: true,
              buttonLabel: "Ok",
              onClose: "/admin/contract-approval/list",
              message: "Pedido de homologação submetido com sucesso.",
            },
          },
        });
      },
      onError: (error) => {
        let errorMessage = "Ocorreu um erro ao submeter o pedido.";
        if (error.response && error.response.data && error.response.data.errors) {
          const errors = error.response.data.errors;
          errorMessage = Object.values(errors).flat().join(". ");
        }
        dispatch({
          type: DIALOG.SHOW,
          payload: {
            showGenericDialog: true,
            genericDialogData: {
              type: DIALOG.ONE_BUTTON_TYPE,
              isSuccess: false,
              message: errorMessage,
            },
          },
        });
      },
    }
  );

  const goBack = () => {
    props.history.push("/admin/contract-approval/list");
  };

  const redirect = true;

  const { mutateAsync: handleDeleteAsync } = useMutation(
    ["createWorkCenterEmployer"],
    async (id) => deleteContractApproval(id),
    {
      onSuccess: () => {
        dispatch({
          type: DIALOG.SHOW,
          payload: {
            showGenericDialog: true,
            genericDialogData: {
              type: DIALOG.ONE_BUTTON_TYPE,
              isSuccess: true,
              goBack: redirect,
              buttonLabel: "Ok",
              onClose: redirect ? "/admin/contract-approval/list" : null,
              message: "Pedido eliminado com sucesso.",
            },
          },
        });
      },
      onError: (error) => {
        console.log(error);
        dispatch({
          type: DIALOG.SHOW,
          payload: {
            showGenericDialog: true,
            genericDialogData: {
              type: DIALOG.ONE_BUTTON_TYPE,
              isSuccess: false,
              message: "Ocorreu um erro a efetuar o seu pedido, por favor tente mais tarde.",
            },
          },
        });
      },
    }
  );

  const deleteRequest = async () => {
    if (!new_contract_approval?.id) {
      throw new Error("O id do pedido não está definido.");
    }
    await handleDeleteAsync(new_contract_approval?.id);
  };

  const showDialog = (message, submessage, yesAction, noAction) => {
    dispatch(showGenericTwoButtonDialogAction(message, submessage, yesAction, noAction));
  };

  const SubmitRequestConfirmation = () => {
    trigger().then((res) => {
      if (!res) {
        return;
      } else {
        showDialog(
          "Submeter Pedido",
          "Tem a certeza que deseja submeter o pedido de homologação de contrato?",
          () => submitRequest(),
          () => {}
        );
      }
    });
  };

  return (
    <Grid container classes={{ root: classes.containerRoot }}>
      <Grid item xs={12} md={12}>
        <LocationHeader section="Homologação de contratos" />
      </Grid>
      <Grid item xs={12} md={9}>
        <PageTitleHeader title={"Criar Pedido de Homologação de contratos"} />
      </Grid>

      <ContractApprovalRequestDetailsCard contractApprovalRequest={new_contract_approval} isDraft={true} />

      <Grid item xs={12} md={12}>
        <Card className={clsx(classes.cardRoot, localClasses.cardMobilePadding)}>
          <form onSubmit={handleSubmit(submitContractApproval)}>
            <Grid container>
              <Grid item xs={12}>
                <Typography variant="h2" style={{ color: "#002e9e", marginBottom: "20px" }}>
                  Centro de Emprego
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormDropdown
                  control={control}
                  label="Província Centro de Emprego"
                  name="provinceId"
                  options={PROVINCES}
                  mappingTo="code"
                  error={!!formState.errors.provinceId}
                  required
                  disabled
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormDropdown
                  control={control}
                  label="Centro de Emprego"
                  name="employmentCenterId"
                  options={EMPLOYMENT_CENTERS}
                  required
                  mappingTo="code"
                  disabled
                  error={!!formState.errors.provinceId}
                />
              </Grid>
            </Grid>
          </form>
        </Card>
      </Grid>
      <Grid item xs={12} md={12}>
        <GenericButton
          typeSubmit={false}
          color="tertiary"
          size="large"
          onClick={() => handleOpenDialog()}
          classes={classes.TableCardHeaderButton}
          className={localClasses.fullWidthMobileButton}
        >
          Adicionar Trabalhador
        </GenericButton>
      </Grid>
      <Grid item xs={12} md={12}>
        <Box
          classes={{ root: classes.tableResultBoxRoot }}
          sx={{
            mt: 3,
          }}
        >
          <ContractApprovalDetailsTable
            page={pageIndex}
            handleChangePage={handleChangePage}
            handleOpenDialog={handleOpenDetailsDialog}
            listEmployee={listEmployee}
            isLoading={isLoading}
          />
        </Box>
      </Grid>
      <Grid item xs={12} md={12}>
        <Typography variant="h5">
          Para adicionar trabalhador ao pedido de homologação de contrato, deverá realizar o seu registo prévio no Meu
          Perfil na secção Trabalhadores. Para registar trabalhador{" "}
          <Link
            style={{
              textDecoration: "underline",
            }}
            to={{
              pathname: "/admin/user-profile",
              state: { tabType: "WorkCentersEmployees" },
            }}
          >
            clique aqui
          </Link>
          .
        </Typography>
      </Grid>
      <Grid
        item
        xs={12}
        md={12}
        className={localClasses.footerActions}
      >
        <ConditionGuard condition={!!new_contract_approval}>
          <GenericButton size="medium" color="tertiaryRed" onClick={() => deleteRequest()}>
            {"Eliminar Pedido"}
          </GenericButton>
        </ConditionGuard>
        <GenericButton size="medium" color="tertiary" onClick={() => goBack()}>
          {"Voltar á Página Inicial"}
        </GenericButton>
        <ConditionGuard condition={!!new_contract_approval}>
          <GenericButton size="medium" color="primary" onClick={() => handleSubmit(SubmitRequestConfirmation())}>
            {"Submeter Pedido"}
          </GenericButton>
        </ConditionGuard>
      </Grid>
      {openDialog && (
        <ContractApprovalWorkerDialog
          openDialog={openDialog}
          handleCloseDialog={handleCloseDialog}
          getDataSetByName={props.getDataSetByName}
          props={props}
          showSearchModal={showSearchModal}
        />
      )}

      {openDetailsDialog && (
        <ContractApprovalWorkerDetailsDialog
          openDialog={openDetailsDialog}
          handleCloseDetailsDialog={handleCloseDetailsDialog}
          getDataSetByName={props.getDataSetByName}
          props={props}
          workerId={selectedId}
          isDraft={true}
          listWorkers={listEmployee}
        />
      )}
    </Grid>
  );
}

const ContractApprovalCreate = (props) => {
  const defaultFormValues = {
    provinceId: 4,
    employmentCenterId: 33,
    pageIndex: 1,
  };

  const form = useForm({
    defaultValues: defaultFormValues,
  });

  return (
    <FormProvider {...form}>
      <ContractApprovalCreateInner props={props} defaultFormValues={defaultFormValues} />
    </FormProvider>
  );
};

const mapStateToProps = (state) => ({ ...state });

const mapDispatchToProps = (dispatch) => ({
  getDataSetByName: (name, addEmptyField) => dispatch(getDataSetByNameAction(name, addEmptyField)),
  listContractApprovalWorkCenterEmployee: (id) => dispatch(listContractApprovalWorkCenterEmployeeAction(id)),
  getWorkCenterEmployeeDocument: (docId, docName) => dispatch(getWorkCenterEmployeeDocumentAction(docId, docName)),
});
export default connect(mapStateToProps, mapDispatchToProps)(ContractApprovalCreate);
